import React, { useContext, useState, useCallback } from "react";
import { Eye, EyeOff, Download, KeyRound, Users } from "lucide-react";
import { AuthContext } from "./AuthContext";
import { API_BASE_URL } from "./api";
import {
  Button,
  Field,
  MetricCard,
  SectionHeader,
  SurfaceCard,
  TextareaField,
} from "./components/designSystem";

export default function Settings() {
  const { user, token } = useContext(AuthContext);
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [teamName, setTeamName] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/update-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.success) setMessage("Profile updated.");
      else setMessage(data.error || "Update failed.");
    } catch (err) {
      setMessage("Error updating profile.");
    }
  };

  const fetchTeams = useCallback(async () => {
    setLoadingTeams(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/teams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTeams(data.teams || []);
    } catch (err) {
      setTeams([]);
    }
    setLoadingTeams(false);
  }, [token]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/api/team`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: teamName, members: teamMembers }),
      });
      const data = await res.json();
      if (data.success) {
        setTeamName("");
        setTeamMembers([]);
        fetchTeams();
      }
    } catch (err) {}
  };

  React.useEffect(() => {
    if (user) fetchTeams();
  }, [user, fetchTeams]);

  const exportMyData = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/export`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const error = await res.json();
        alert(error.error || "Failed to export trades.");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "trades.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to export trades.");
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.12),_transparent_20%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.12),_transparent_20%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Account Settings"
          title="Control profile, exports, and workspace access"
          description="A premium trading journal should make account operations feel clear, calm, and trustworthy rather than buried in utility panels."
        />
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Plan" value={user?.plan ? user.plan.toUpperCase() : "FREE"} helper="Current entitlement tier." />
          <MetricCard label="API Access" value={user?.plan === "pro" || user?.plan === "enterprise" ? "Enabled" : "Locked"} helper="Advanced integrations by subscription tier." />
          <MetricCard label="Teams" value={String(teams.length)} helper="Shared workspaces available to this account." />
          <MetricCard label="Admin" value={user?.isAdmin ? "Yes" : "No"} helper="Operational privileges on this account." />
        </div>
      </SurfaceCard>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SurfaceCard>
          <SectionHeader eyebrow="Profile" title="Update account credentials" description="Keep contact details and password current so support and recovery flows remain reliable." />
          <form onSubmit={handleProfileUpdate} className="mt-6 space-y-5">
            <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">New Password</span>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11 text-sm text-slate-900 outline-none transition focus:border-slate-400 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:focus:border-gray-500"
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-slate-500 dark:text-slate-400"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" variant="primary">Update Profile</Button>
              {message ? <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{message}</p> : null}
            </div>
          </form>
        </SurfaceCard>

        <div className="space-y-6">
          {(user?.plan === "pro" || user?.plan === "enterprise") && (
            <SurfaceCard>
              <SectionHeader eyebrow="Subscription & Data" title="Export and integration controls" description="Give serious traders a clear path to ownership of their data and external connectivity." />
              <div className="mt-6 space-y-5">
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex items-start gap-3">
                    <Download className="mt-1 h-5 w-5 text-sky-600 dark:text-sky-300" />
                    <div>
                      <p className="text-sm font-semibold text-slate-950 dark:text-white">Export my trading data</p>
                      <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                        Download your trade history as CSV for backup, audit, or external modeling.
                      </p>
                    </div>
                  </div>
                  <Button type="button" variant="brand" className="mt-5" onClick={exportMyData}>Export CSV</Button>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex items-start gap-3">
                    <KeyRound className="mt-1 h-5 w-5 text-amber-600 dark:text-amber-300" />
                    <div>
                      <p className="text-sm font-semibold text-slate-950 dark:text-white">API token support</p>
                      <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                        To request your API token, contact support. Tokens are delivered to the registered account email.
                      </p>
                    </div>
                  </div>
                  <TextareaField
                    label="Integration notes"
                    value={`GET /trades\nGET /analytics\nGET /export\nPOST /custom-report`}
                    readOnly
                    rows={5}
                    inputClassName="resize-none"
                    className="mt-5"
                  />
                </div>
              </div>
            </SurfaceCard>
          )}

          <SurfaceCard>
            <SectionHeader eyebrow="Teams" title="Shared workspace management" description="Enterprise owners can create teams here, while all users can view their linked workspaces." />
            {user?.plan === "enterprise" ? (
              <form onSubmit={handleCreateTeam} className="mt-6 space-y-5">
                <Field label="Team Name" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="MekEdge Alpha Desk" />
                <Field
                  label="Members"
                  value={teamMembers.join(", ")}
                  onChange={(e) => setTeamMembers(e.target.value.split(/,\s*/).filter(Boolean))}
                  placeholder="user1@email.com, user2@email.com"
                />
                <Button type="submit" variant="primary">Create Team</Button>
              </form>
            ) : null}

            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-slate-500 dark:text-slate-300" />
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Your teams</p>
              </div>
              {loadingTeams ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading teams...</p>
              ) : teams.length === 0 ? (
                <p className="text-sm leading-7 text-slate-500 dark:text-slate-400">
                  No teams found.{user?.plan === "enterprise" ? " Create one above." : " You will see teams here when added by an enterprise owner."}
                </p>
              ) : (
                teams.map((team) => (
                  <div key={team._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{team.name}</p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Members: {team.members.length}</p>
                      </div>
                      <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 dark:border-gray-700 dark:text-slate-300">Workspace</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}
